/**
 * What a native date input can show for a date field, which is not the same as what the
 * field holds.
 *
 * The two date fields on the postcard form are free text and take either a day
 * ("2026-09-08") or a month ("2026-09") — `formatDate` on the home page renders both, and
 * cards are stored both ways. `<input type="date">` has no way to hold a month, no way to
 * hold a partial date, and rejects a day that does not exist. So the text stays the field
 * that is submitted and the picker is a second control pointed at it, reading through
 * here: anything a date input cannot honestly represent leaves it blank.
 *
 * Blank rather than approximate, because the picker's value is what gets written back to
 * the text. Opening a month on the first of that month would read as helpful and would
 * quietly turn "Sep 2026" into 1 Sep 2026, moving the card in a gallery sorted by that
 * very field. A blank picker costs one click and cannot lose what she typed.
 */
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function pickerValue(written: string) {
  const match = ISO_DAY.exec(written.trim());
  if (!match) return "";
  const [value, year, month, day] = match;
  // Round-tripping through Date is what catches 31 September and 29 February in a common
  // year: Date rolls an impossible day into the next month rather than refusing it, so the
  // date that comes back out is the test of the one that went in. UTC because this is a
  // calendar date and not a moment — building it locally would shift it a day either side
  // of midnight depending on where the browser is.
  const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const sameDay = parsed.getUTCFullYear() === Number(year)
    && parsed.getUTCMonth() === Number(month) - 1
    && parsed.getUTCDate() === Number(day);
  return sameDay ? value : "";
}

/** A stored date as the page shows it: a day, a bare month, a bare year, or "Unknown". */
export function formatDate(date: string) {
  if (!date) return "Unknown";
  if (/^\d{4}-\d{2}$/.test(date)) {
    const [year, month] = date.split("-").map(Number);
    return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" })
      .format(new Date(Date.UTC(year, month - 1, 1)));
  }
  if (/^\d{4}$/.test(date)) return date;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
}
